import React, { useMemo, useState } from "react";
import SingleSelect from "@/components/controls/SingleSelect";
import { useUsersInfinite } from "@/entities/users/hooks";
import { UserStatus } from "@/entities/users/types";
import {
  matchingOptionEmail,
  ownerOptions
} from "@/pages/settings/components/integrations/hubspot/hubspotSettingsOptions";

const ACTIVE_USER_FILTERS = { status: [UserStatus.ACTIVE] };

interface OwnerSelectProps {
  fieldName: string;
  email: string | null;
  onChange: (email: string | null) => void;
  placeholder: string;
  unknownUserLabel: string;
  disabled: boolean;
  className?: string;
  popoverTriggerClassName?: string;
}

// Searches active users on the server, so a tenant with more users than one
// page can still pick any of them.
export const OwnerSelect: React.FC<OwnerSelectProps> = ({
  fieldName,
  email,
  onChange,
  placeholder,
  unknownUserLabel,
  disabled,
  className,
  popoverTriggerClassName
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const search = useMemo(
    () => (searchTerm ? { term: searchTerm } : undefined),
    [searchTerm]
  );
  const { entities, isLoading } = useUsersInfinite(ACTIVE_USER_FILTERS, search);

  // The users search matches emails too, so the saved owner is found by theirs
  // whether or not the list above happens to include them.
  const savedOwnerSearch = useMemo(
    () => (email ? { term: email } : undefined),
    [email]
  );
  const savedOwnerLookup = useUsersInfinite(
    ACTIVE_USER_FILTERS,
    savedOwnerSearch
  );

  // A failed lookup found nothing either way, so it doesn't mark them unknown.
  const savedOwnerSettled =
    !savedOwnerLookup.isLoading && !savedOwnerLookup.isError;
  const options = useMemo(
    () =>
      ownerOptions(
        entities,
        email,
        savedOwnerSettled ? savedOwnerLookup.entities : undefined,
        unknownUserLabel
      ),
    [
      entities,
      email,
      savedOwnerSettled,
      savedOwnerLookup.entities,
      unknownUserLabel
    ]
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSearchTerm("");
  };

  return (
    <SingleSelect
      fieldName={fieldName}
      options={options}
      selectedValue={matchingOptionEmail(options, email) ?? undefined}
      onSelection={(value) => onChange(String(value))}
      formatter={(value) =>
        options.find((option) => option.id === value)?.displayName ??
        String(value)
      }
      placeholder={placeholder}
      clearable
      onClear={() => onChange(null)}
      disabled={disabled}
      showSearch
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      onSearch={setSearchTerm}
      isLoading={isLoading}
      className={className}
      popoverTriggerClassName={popoverTriggerClassName}
      variant="form"
    />
  );
};
